/**
 * Contact display names.
 *
 * `contacts_contacts.first_name` and `.last_name` are both NULLable — a contact
 * is valid with only one of them — so a template literal over the raw fields
 * renders the literal string `"null"` ("Eisenberg null" in the contact pickers).
 * These helpers are the single seam that composes the two parts; call them
 * instead of interpolating the columns, and `tools/check_nullable_name_interpolation.py`
 * keeps new call sites from re-opening the hole.
 */

/** The subset of a contact these helpers read — anything contact-shaped satisfies it. */
export interface ContactNameParts {
  first_name?: string | null;
  last_name?: string | null;
}

const present = (value: string | null | undefined): string => value?.trim() ?? '';

const nameParts = (contact: ContactNameParts): string[] =>
  [present(contact.first_name), present(contact.last_name)].filter(Boolean);

/**
 * Display name: the present name parts joined, or an empty string for a contact
 * with neither. The fallback is deliberately the caller's — surfaces disagree on
 * it (`|| contact.email || '—'` in the pickers, `|| t('contacts.unnamed')` on the
 * detail page), and baking one in here would change their behaviour.
 */
export function getContactDisplayName(contact: ContactNameParts): string {
  return nameParts(contact).join(' ');
}

/** Lowercased "last first" key for client-side sorting and search matching. */
export function getContactSortName(contact: ContactNameParts): string {
  return [present(contact.last_name), present(contact.first_name)]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

/** Up to two initials from the present name parts, or '?' for a contact with neither. */
export function getContactInitials(contact: ContactNameParts): string {
  return (
    nameParts(contact)
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase() || '?'
  );
}
